/**
 * Organisation display timezone (Business → Timezone).
 *
 * Stored timestamps are naive UTC. Format them in the IANA zone org
 * admins picked so Uni Forms submissions and SignalBridge order times
 * agree.
 */
import { useEffect, useState } from 'react';

import { getJSON } from '../api/client.js';
import { API } from '../config.js';
import { useAuth } from './auth.jsx';
import { writeStoredUser } from './authStorage.js';

export const DEFAULT_DISPLAY_TIMEZONE = 'Asia/Kolkata';
export const TZ_EVENT = 'org-timezone';

export const COMMON_TIMEZONES = [
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Dhaka',
  'Asia/Karachi',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Australia/Sydney',
  'Pacific/Auckland',
  'Africa/Nairobi',
  'UTC',
];

export function browserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_DISPLAY_TIMEZONE;
  } catch {
    return DEFAULT_DISPLAY_TIMEZONE;
  }
}

export function commonTimeZones() {
  const extra = (() => {
    try {
      if (typeof Intl.supportedValuesOf === 'function') {
        return Intl.supportedValuesOf('timeZone') || [];
      }
    } catch {
      return [];
    }
    return [];
  })();
  const seen = new Set();
  const out = [];
  [...COMMON_TIMEZONES, browserTimeZone(), ...extra].forEach((name) => {
    if (name && !seen.has(name)) {
      seen.add(name);
      out.push(name);
    }
  });
  return out;
}

export function parseUtcDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value < 1e12 ? value * 1000 : value);
  }
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^\d{10,13}$/.test(raw)) {
    const n = Number(raw);
    return new Date(n < 1e12 ? n * 1000 : n);
  }
  const hasTz = /Z$/i.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw);
  const normalized = hasTz ? raw : `${raw.replace(' ', 'T')}Z`;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatInTimeZone(value, timeZone) {
  const d = parseUtcDate(value);
  if (!d) return '—';
  const tz = timeZone || DEFAULT_DISPLAY_TIMEZONE;
  try {
    return new Intl.DateTimeFormat('en-IN', {
      timeZone: tz,
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    }).format(d);
  } catch {
    return d.toISOString();
  }
}

export function timezoneFromUser(user) {
  const raw = user && user.display_timezone;
  return (typeof raw === 'string' && raw.trim()) ? raw.trim() : '';
}

export function publishOrgTimeZone(tz, user) {
  if (!tz) return;
  try {
    window.dispatchEvent(new CustomEvent(TZ_EVENT, { detail: tz }));
  } catch {
    /* ignore */
  }
  if (user) {
    writeStoredUser({ ...user, display_timezone: tz });
  }
}

export function useOrgTimeZone() {
  const { user, setUser } = useAuth();
  const fromUser = timezoneFromUser(user);
  const [tz, setTz] = useState(fromUser || DEFAULT_DISPLAY_TIMEZONE);

  useEffect(() => {
    if (fromUser) {
      setTz(fromUser);
      return undefined;
    }
    let cancelled = false;
    getJSON(API.ENDPOINTS.BUSINESS_TIMEZONE)
      .then((data) => {
        const next = (data && data.display_timezone) || DEFAULT_DISPLAY_TIMEZONE;
        if (!cancelled) setTz(next);
      })
      .catch(() => {
        if (!cancelled) setTz((current) => current || DEFAULT_DISPLAY_TIMEZONE);
      });
    return () => { cancelled = true; };
  }, [fromUser]);

  useEffect(() => {
    const onCustom = (e) => {
      if (!e || !e.detail) return;
      setTz(e.detail);
      if (user && setUser) {
        const next = { ...user, display_timezone: e.detail };
        setUser(next);
        writeStoredUser(next);
      }
    };
    window.addEventListener(TZ_EVENT, onCustom);
    return () => window.removeEventListener(TZ_EVENT, onCustom);
  }, [user, setUser]);

  return tz || DEFAULT_DISPLAY_TIMEZONE;
}
